import { useMemo } from "react"

import { useTranslation } from "react-i18next"
import { CartesianGrid, Scatter, ScatterChart, XAxis, YAxis, ZAxis } from "recharts"

import type { DashboardUseCase } from "../../types/use-case.types"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"

import { usdFormatter } from "./usd-tooltip-formatter"
import { FEASIBILITY_MAP } from "../../types/use-case.types"
import { formatUSD } from "../../utils/format-helpers"

type InvestmentBenefitScatterProps = {
  useCases: DashboardUseCase[]
}

type ScatterPoint = {
  x: number
  y: number
  name: string
  feasibility: string
}

const FEAS_ENTRIES = Object.entries(FEASIBILITY_MAP) as [string, { label: string; color: string }][]

export function InvestmentBenefitScatter({ useCases }: InvestmentBenefitScatterProps) {
  const { t } = useTranslation()

  const chartConfig = useMemo(
    () =>
      ({
        x: { label: t("useCases.dashboard.charts.investment") },
        y: { label: t("useCases.dashboard.charts.annualBenefit") },
      }) satisfies ChartConfig,
    [t]
  )

  const grouped = useMemo(() => {
    const groups: Record<string, ScatterPoint[]> = {}
    for (const [key] of FEAS_ENTRIES) {
      groups[key] = []
    }

    for (const uc of useCases) {
      const inv = parseFloat(uc.investment)
      if (isNaN(inv)) continue
      const point: ScatterPoint = {
        x: inv,
        y: uc.annualBenefit,
        name: uc.ucName,
        feasibility: uc.feasV,
      }
      ;(groups[uc.feasV] ??= []).push(point)
    }

    return groups
  }, [useCases])

  const hasData = Object.values(grouped).some((g) => g.length > 0)
  if (!hasData) return null

  return (
    <Card className="rounded-lg shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {t("useCases.dashboard.charts.investmentVsBenefit")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer className="h-[300px] w-full" config={chartConfig}>
          <ScatterChart margin={{ left: 10, right: 30, top: 5, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="x"
              name={t("useCases.dashboard.charts.investment")}
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => formatUSD(v)}
              type="number"
            />
            <YAxis
              dataKey="y"
              name={t("useCases.dashboard.charts.annualBenefit")}
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => formatUSD(v)}
              type="number"
            />
            <ZAxis range={[40, 40]} />
            <ChartTooltip
              content={<ChartTooltipContent />}
              formatter={usdFormatter}
              labelFormatter={(_label, payload) => payload[0]?.payload?.name ?? _label}
            />
            {FEAS_ENTRIES.map(([key, config]) => (
              <Scatter
                key={key}
                data={grouped[key]}
                fill={config.color}
                name={config.label}
                stroke="hsl(var(--foreground))"
                strokeWidth={1}
              />
            ))}
          </ScatterChart>
        </ChartContainer>
        <div className="mt-2 flex justify-center gap-4">
          {FEAS_ENTRIES.map(([key, config]) => (
            <div key={key} className="flex items-center gap-1.5 text-xs">
              <div
                className="border-foreground size-2.5 rounded-full border"
                style={{ backgroundColor: config.color }}
              />
              <span>{config.label}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
